import { useState } from "react";
import { Link } from "react-router-dom";

import { CONTACT_EMAIL, CONTACT_MAILTO } from "../../lib/contact";
import { PLANNED_FEATURE_GROUPS } from "../../lib/plannedFeatures";
import { ResponsiveTable } from "../../components/ui/ResponsiveTable";

const TOC = [
  { id: "po-co", label: "Po co to jest?" },
  { id: "pierwsze-minuty", label: "Pierwsze 10 minut" },
  { id: "mapa", label: "Mapa aplikacji" },
  { id: "panel", label: "Pulpit i Szukaj owcy" },
  { id: "statystyki", label: "Statystyki stada" },
  { id: "zwierzeta", label: "Zwierzęta" },
  { id: "stanowka-wykoty", label: "Stanówka i wykoty" },
  { id: "zasoby", label: "Zasoby — wolne kolczyki" },
  { id: "weryfikacja", label: "Kontrole" },
  { id: "raporty", label: "Raporty" },
  { id: "finanse", label: "Finanse" },
  { id: "irz", label: "IRZ" },
  { id: "planowane", label: "Planowane rozszerzenia" },
  { id: "ustawienia", label: "Ustawienia, MFA i bezpieczeństwo" },
  { id: "offline", label: "Telefon, teren i offline" },
  { id: "dni", label: "Typowe dni z aplikacją" },
  { id: "faq", label: "Częste pytania" },
  { id: "pomoc", label: "Gdy coś nie gra" },
  { id: "checklista", label: "Checklista na start" },
] as const;

const CHECKLIST_STORAGE_KEY = "guide-start-checklist";

const CHECKLIST_ITEMS = [
  { id: "account", label: "Mam konto i potwierdzony e-mail (albo logowanie Google)" },
  {
    id: "admin-password",
    label: "Ustawiłem hasło administratora w Ustawieniach (do wrażliwych operacji)",
  },
  {
    id: "mfa",
    label: "(Opcjonalnie) Włączyłem uwierzytelnianie dwuskładnikowe (MFA) i zapisałem kody zapasowe",
  },
  { id: "animals", label: "Dodałem pierwsze zwierzęta albo wczytałem dane przykładowe" },
  {
    id: "season",
    label: "Utworzyłem grupę stanówki lub sezon wykotów (jeśli jesteście w sezonie)",
  },
  {
    id: "tags-pool",
    label: "(Opcjonalnie) Wkleiłem pulę wolnych kolczyków urzędowych w Zasobach",
  },
  { id: "tags", label: "Przetestowałem krótką kontrolę stada (weryfikacja kolczyków)" },
  { id: "finance", label: "Dodałem jedną transakcję w Finansach" },
  {
    id: "pwa",
    label: "(Opcjonalnie) Zainstalowałem aplikację na telefonie i sprawdziłem status połączenia",
  },
] as const;

type ChecklistState = Record<string, boolean>;

function loadChecklistState(): ChecklistState {
  try {
    const raw = localStorage.getItem(CHECKLIST_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as ChecklistState).filter(([, value]) => typeof value === "boolean"),
    );
  } catch {
    return {};
  }
}

export function GuidePage() {
  const [checklistDone, setChecklistDone] = useState<ChecklistState>(loadChecklistState);

  function toggleChecklistItem(id: string) {
    setChecklistDone((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }

  return (
    <main className="page guide-page" data-testid="guide-page">
      <header className="page__header guide-hero">
        <p className="page__kicker">Jak korzystać z aplikacji</p>
        <h1>Przewodnik</h1>
        <p className="muted guide-hero__lead">
          Stado, wykoty, kolczyki, finanse i IRZ — w jednym miejscu. Ten przewodnik pokazuje,{" "}
          <strong>co aplikacja potrafi teraz</strong>, <strong>co planujemy dodać wkrótce</strong>{" "}
          i <strong>jak z tego korzystać na co dzień</strong>, bez żargonu i bez przeciągania.
        </p>
        <p className="guide-callout">
          Potrzebujesz pomocy administratora? Napisz na{" "}
          <a className="guide-inline-link" href={CONTACT_MAILTO}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </header>

      <nav className="guide-toc panel" aria-label="Spis treści przewodnika">
        <h2 className="guide-toc__title">Spis treści</h2>
        <ol className="guide-toc__list">
          {TOC.map((item) => (
            <li key={item.id}>
              <a href={`#${item.id}`}>{item.label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <article id="po-co" className="panel guide-section">
        <h2 className="guide-section__title">Po co to jest?</h2>
        <p className="guide-section__text">
          Papierowe listy rozmazują się. Zeszyty giną. „Gdzieś w telefonie” kończy się chaotycznymi
          notatkami. <strong>AgroHodowca</strong> zbiera to, czego naprawdę potrzebujesz przy hodowli
          owiec:
        </p>
        <ul className="guide-list">
          <li>aktualny rejestr stada (i archiwum odejść),</li>
          <li>stanówki, pokrycia, sezony wykotów i historię urodzeń,</li>
          <li>pulę wolnych kolczyków urzędowych,</li>
          <li>kontrolę kolczyków w terenie,</li>
          <li>przychody i wydatki gospodarstwa,</li>
          <li>pracę z danymi IRZplus,</li>
          <li>raporty PDF i liczby, które trudno policzyć „na oko”.</li>
        </ul>
        <p className="guide-section__text">
          Działa w przeglądarce — na komputerze i na telefonie. Możesz też zainstalować ją jak zwykłą
          aplikację i pracować w pełni offline w oborze. Każde konto ma <strong>własne stado</strong> —
          dane nie mieszają się między użytkownikami.
        </p>
        <p className="guide-callout">
          Nie musisz być „od komputerów”. Wystarczy otworzyć stronę, zalogować się i iść od góry
          menu.
        </p>
      </article>

      <article id="pierwsze-minuty" className="panel guide-section">
        <h2 className="guide-section__title">Pierwsze 10 minut</h2>

        <h3 className="guide-section__subtitle">1. Wejdź i załóż konto</h3>
        <ol className="guide-steps">
          <li>Otwórz adres aplikacji.</li>
          <li>
            Na ekranie logowania wybierz <strong>Zarejestruj się</strong> — albo od razu{" "}
            <strong>Zaloguj przez Google</strong> (na rejestracji:{" "}
            <strong>Kontynuuj z Google</strong>).
          </li>
          <li>
            Przy rejestracji e-mailem: hasło min. 8 znaków. Po zapisie zobaczysz ekran{" "}
            <strong>Sprawdź skrzynkę e-mail</strong> — kliknij link potwierdzający (ważny 24
            godziny).
          </li>
          <li>
            Wróć i zaloguj się. Opcjonalnie zaznacz{" "}
            <strong>Zapamiętaj mnie (sesja 7 dni)</strong>. Jeśli nie pamiętasz hasła —{" "}
            <strong>Zapomniałem hasła</strong> na ekranie logowania.
          </li>
        </ol>
        <p className="guide-section__text">
          Czasem pojawi się krótka weryfikacja (captcha), że to nie robot. Jeśli włączysz MFA w
          Ustawieniach, po haśle aplikacja poprosi jeszcze o kod z aplikacji uwierzytelniającej.
        </p>

        <h3 className="guide-section__subtitle">2. Zobacz Panel główny</h3>
        <p className="guide-section__text">
          Po zalogowaniu lądujesz na{" "}
          <Link className="guide-inline-link" to="/">
            Pulpicie
          </Link>{" "}
          (nagłówek: <strong>Panel główny</strong>). U góry cztery kafelki: ile zwierząt w stadzie,
          co wymaga uwagi, ile otwartych zadań oraz <strong>Statystyki → Więcej</strong>. Niżej —
          szybkie akcje, lista spraw do zrobienia, zadania, ostatnia aktywność i finanse{" "}
          <strong>tego miesiąca</strong>.
        </p>
        <p className="guide-section__text">
          Pod kafelkami masz <strong>szybkie akcje</strong> — bez szukania po menu:
        </p>
        <ul className="guide-list guide-list--inline">
          <li>Szukaj owcy</li>
          <li>Dodaj zwierzę</li>
          <li>Stanówka</li>
          <li>Nowy wykot</li>
          <li>Kontrola stada</li>
          <li>Dodaj transakcję</li>
          <li>Generuj raport</li>
        </ul>

        <h3 className="guide-section__subtitle">3. Dodaj pierwszą owcę</h3>
        <ol className="guide-steps">
          <li>
            Menu →{" "}
            <Link className="guide-inline-link" to="/animals">
              Zwierzęta
            </Link>{" "}
            → <strong>Dodaj zwierzę</strong>.
          </li>
          <li>
            <strong>Numer kolczyka</strong> jest najważniejszy. Przy zapisie podasz{" "}
            <strong>hasło administratora</strong> (jeśli jeszcze go nie ustawiłeś — zrób to w{" "}
            <Link className="guide-inline-link" to="/settings">
              Ustawieniach
            </Link>
            ).
          </li>
          <li>Zapisz.</li>
        </ol>
        <p className="guide-section__text">
          Gotowe. Masz konto, jesteś w aplikacji i masz pierwszą pozycję w rejestrze. Resztę
          uzupełnisz przy okazji — nie musisz wypełniać wszystkiego od razu.
        </p>
        <p className="guide-tip">
          <strong>Chcesz poćwiczyć bez własnych danych?</strong> W belce u góry jest przycisk{" "}
          <strong>Przykłady</strong> — wczyta zestaw przykładowych owiec. Twoje istniejące zwierzęta
          nie zostaną nadpisane (duplikaty pomijane). Potem możesz usunąć cały zestaw jednym ruchem.
        </p>
      </article>

      <article id="mapa" className="panel guide-section">
        <h2 className="guide-section__title">Mapa aplikacji — co jest w menu?</h2>
        <p className="guide-section__text">
          W bocznym menu (na telefonie — pod ikoną ☰) sekcje{" "}
          <strong>Codzienna praca</strong> (Pulpit, Zwierzęta, Rozród, Kontrole) i{" "}
          <strong>Gospodarstwo</strong> (Zasoby, IRZ, Raporty, Finanse), a na dole{" "}
          <strong>Przewodnik</strong> i <strong>Ustawienia</strong>. W wielu modułach pojawia się też{" "}
          <strong>subnawigacja</strong> — dodatkowe zakładki u góry strony.
        </p>
        <div className="guide-table-wrap">
          <ResponsiveTable className="guide-table">
            <thead>
              <tr>
                <th scope="col">W menu</th>
                <th scope="col">Kiedy tam idziesz</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/">
                    Pulpit
                  </Link>
                </td>
                <td>
                  Rano / między zadaniami — „co się dzieje?” (strona: <strong>Panel główny</strong>)
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/animals">
                    Zwierzęta
                  </Link>
                </td>
                <td>
                  Rejestr stada — subnawigacja: <strong>Stado</strong>,{" "}
                  <Link className="guide-inline-link" to="/animals/archive">
                    Archiwum
                  </Link>
                  ,{" "}
                  <Link className="guide-inline-link" to="/irz">
                    Porównanie IRZ
                  </Link>
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/reproduction">
                    Rozród
                  </Link>
                </td>
                <td>
                  Tworzenie grup oraz zakładki{" "}
                  <Link className="guide-inline-link" to="/reproduction/breeding">
                    Stanówka
                  </Link>{" "}
                  i{" "}
                  <Link className="guide-inline-link" to="/reproduction/births">
                    Wykoty
                  </Link>
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/reproduction/births">
                    Wykoty
                  </Link>
                </td>
                <td>
                  Urodzenia, wagi, kolczyki — subnawigacja: <strong>Aktywne</strong>,{" "}
                  <strong>Archiwum</strong>
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/resources">
                    Zasoby
                  </Link>
                </td>
                <td>
                  Pula wolnych kolczyków urzędowych (zakładka: <strong>Wolne kolczyki</strong>)
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/controls">
                    Kontrole
                  </Link>
                </td>
                <td>
                  Czynności kontrolne w stadzie — subnawigacja: <strong>Aktywna kontrola</strong>,{" "}
                  <Link className="guide-inline-link" to="/controls/history">
                    Historia
                  </Link>
                  . Dziś: weryfikacja kolczyków; na liście widać też ważenie matek, szczepienia i
                  odrobaczanie — na razie z adnotacją „Wkrótce”.
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/reports">
                    Raporty
                  </Link>
                </td>
                <td>
                  PDF z zakończonych sesji weryfikacji kolczyków (strona:{" "}
                  <strong>Generowanie raportów</strong>)
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/expenses">
                    Finanse
                  </Link>
                </td>
                <td>
                  Wydatki, przychody, kategorie, zdjęcia paragonów (strona:{" "}
                  <strong>Finanse hodowli</strong>)
                </td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/irz">
                    IRZ
                  </Link>
                </td>
                <td>Logowanie IRZplus, import, synchronizacja, porównanie ze stadem</td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/guide">
                    Przewodnik
                  </Link>
                </td>
                <td>Ten tekst — jak zacząć i jak korzystać z modułów</td>
              </tr>
              <tr>
                <td>
                  <Link className="guide-inline-link" to="/settings">
                    Ustawienia
                  </Link>
                </td>
                <td>
                  Hasło logowania, MFA, hasło administratora, preferencje potwierdzania, czyszczenie
                  danych i usunięcie konta
                </td>
              </tr>
            </tbody>
          </ResponsiveTable>
        </div>
        <p className="guide-section__text">
          <Link className="guide-inline-link" to="/statistics">
            Statystyki stada
          </Link>{" "}
          nie ma osobnej pozycji w menu — wejdziesz z kafelka <strong>Statystyki → Więcej</strong> na
          Pulpicie albo ze skrótu w <strong>Szukaj owcy</strong>.
        </p>
        <p className="guide-section__text">
          Menu możesz zwinąć przyciskiem w nagłówku. W belce u góry:{" "}
          <strong>Przykłady</strong>, ikona statusu połączenia i <strong>Wyloguj</strong>.
        </p>
      </article>

      <article id="panel" className="panel guide-section">
        <h2 className="guide-section__title">Pulpit i Szukaj owcy — stan gospodarstwa w jednym spojrzeniu</h2>
        <p className="guide-section__text">
          <Link className="guide-inline-link" to="/">
            Pulpit
          </Link>{" "}
          (nagłówek strony: <strong>Panel główny</strong>) nie jest „ładną ścianą liczb”. Ma pomóc Ci
          zdecydować, <strong>co zrobić teraz</strong>.
        </p>
        <p className="guide-section__text">Zobaczysz m.in.:</p>
        <ul className="guide-list">
          <li>
            kafelki: <strong>Zwierzęta w stadzie</strong>, <strong>Wymaga uwagi</strong>,{" "}
            <strong>Otwarte zadania</strong>, <strong>Statystyki → Więcej</strong>,
          </li>
          <li>
            <strong>szybkie akcje</strong> pod kafelkami, a niżej blok{" "}
            <strong>Do zrobienia teraz</strong> — sprawy ze stada z przyciskiem do od razu przejścia
            do akcji (Pilne / Ważne / Info),
          </li>
          <li>
            podsumowanie finansów <strong>tego miesiąca</strong> na dole strony (przychód, wydatek,
            bilans) — skrót do Finansów.
          </li>
        </ul>
        <p className="guide-section__text">
          <strong>Do zrobienia teraz</strong> potrafi m.in. przypomnieć: niedokończoną kontrolę
          stada, zaległe albo zbliżające się ważenia, matki bez wykotu, jagnięta bez oficjalnego
          kolczyka, zadania po terminie, brak zwierząt albo brak sezonu wykotów.
        </p>
        <p className="guide-section__text">
          <strong>Moje zadania</strong> — krótkie TODO z terminem. Idealne na: „jutro ważenie”,
          „sprawdź matkę bez wykotu”, „dowieźć paszę”. Dodajesz je przyciskiem{" "}
          <strong>Dodaj zadanie</strong>. Zakładki <strong>Aktywne</strong> /{" "}
          <strong>Zakończone</strong> — zadanie zamykasz przyciskiem <strong>Zakończ</strong>,
          usuwasz przez <strong>Usuń</strong>. Po terminie pozycje są wyróżnione na liście.
        </p>
        <p className="guide-section__text">
          <strong>Ostatnia aktywność</strong> przypomina, co ostatnio działo się w stadzie —
          wykoty, finanse, zakończone kontrole — między oborą a biurem łatwo to zgubić.
        </p>

        <h3 className="guide-section__subtitle">Szukaj owcy — skrót w terenie</h3>
        <p className="guide-section__text">
          Przycisk <strong>Szukaj owcy</strong> w szybkich akcjach otwiera wyszukiwarkę po numerze
          kolczyka. Tryb <strong>Ręcznie</strong> (z prefiksem <code>PL1000</code>) albo{" "}
          <strong>Automatycznie</strong> — tak jak w kontroli. Przy wpisywaniu pojawiają się
          podpowiedzi ze stada. Po znalezieniu zwierzęcia możesz od razu:
        </p>
        <ul className="guide-list">
          <li>
            otworzyć <strong>Informacje</strong> (pełne szczegóły, historia IRZ, potomstwo,
            przemieszczenia),
          </li>
          <li>
            odnotować <strong>Pokrycie</strong> albo <strong>Zgłoś wykot</strong>,
          </li>
          <li>
            dodać <strong>Wydatek</strong> albo <strong>Przychód</strong> powiązany z owcą,
          </li>
          <li>
            utworzyć <strong>Zadanie</strong>,
          </li>
          <li>
            zobaczyć <strong>Statystyki</strong> tej sztuki (wiek, potomstwo, pokrycia, wykoty,
            finanse),
          </li>
          <li>
            pobrać <strong>Raport PDF</strong> zwierzęcia.
          </li>
        </ul>
        <p className="guide-section__text">
          Po akcji aplikacja pyta, czy szukać kolejnej sztuki — wygodne, gdy stoisz przy kilku
          owcach pod rząd.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/">
            Przejdź do Pulpitu
          </Link>
        </p>
      </article>

      <article id="statystyki" className="panel guide-section">
        <h2 className="guide-section__title">Statystyki stada — liczby z kontekstem</h2>
        <p className="guide-section__text">
          Strona{" "}
          <Link className="guide-inline-link" to="/statistics">
            Statystyki stada
          </Link>{" "}
          zbiera podsumowania, które trudno szybko policzyć „na oko”. Na górze: ile sztuk w stadzie,
          średni wiek, jagnięta łącznie i odejścia. Niżej rozwijane bloki:
        </p>
        <ul className="guide-list">
          <li>
            <strong>Stado aktywne</strong> — płeć, wiek (w tym mediana), program / licencja, rasy,
            sztuki bez daty urodzenia albo bez matki,
          </li>
          <li>
            <strong>Rozród</strong> — wykoty i stanówki; zakres{" "}
            <strong>Wszystkie / Aktywne / Archiwum</strong> (średni miot, pokrycie, jagnięta bez
            oficjalnego kolczyka, tabele sezonów),
          </li>
          <li>
            <strong>Odejścia</strong> — sprzedaż, padnięcie, ubój,
          </li>
          <li>
            <strong>Kolczyki urzędowe</strong> — wolne vs przypisane.
          </li>
        </ul>
        <p className="guide-section__text">
          Przy wielu liczbach jest podpowiedź <strong>Co to oznacza?</strong>. Wejdziesz stąd z
          kafelka na Pulpicie albo ze skrótu w <strong>Szukaj owcy</strong>.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/statistics">
            Otwórz Statystyki
          </Link>
        </p>
      </article>

      <article id="zwierzeta" className="panel guide-section">
        <h2 className="guide-section__title">Zwierzęta — żywy rejestr stada</h2>
        <p className="guide-section__text">
          Zakładka <strong>Stado</strong> to Twoje aktywne owce: kolczyki, rasa, płeć, daty, status,{" "}
          powiązania (np. matka), pole <strong>Program</strong> (Nie określono / Brak licencji /
          Licencja / W programie) oraz szczegóły zsynchronizowane z IRZ.
        </p>

        <h3 className="guide-section__subtitle">Co możesz zrobić</h3>
        <ul className="guide-list">
          <li>
            dodać nową sztukę przyciskiem <strong>Dodaj zwierzę</strong>,
          </li>
          <li>
            edytować dane (<strong>Edytuj</strong> — wymaga hasła administratora),
          </li>
          <li>
            szukać po kolczyku, filtrować po płci i programie, sortować kolumny tabeli,
          </li>
          <li>
            kliknąć numer kolczyka — otworzą się <strong>Szczegóły zwierzęcia</strong> (dane IRZ,
            środki identyfikacji, potomstwo, zdarzenia, przemieszczenia),
          </li>
          <li>
            porównać lokalną bazę z IRZplus (
            <Link className="guide-inline-link" to="/irz">
              Porównanie IRZ
            </Link>
            ),
          </li>
          <li>
            przenieść sztukę do{" "}
            <Link className="guide-inline-link" to="/animals/archive">
              Archiwum
            </Link>{" "}
            przyciskiem <strong>Zdarzenie</strong> (powód: padło / sprzedane / ubój).
          </li>
        </ul>

        <h3 className="guide-section__subtitle">Jak dodać zwierzę</h3>
        <ol className="guide-steps">
          <li>
            <Link className="guide-inline-link" to="/animals">
              Zwierzęta → Stado
            </Link>{" "}
            → <strong>Dodaj zwierzę</strong>.
          </li>
          <li>
            Wpisz numer kolczyka (przy ręcznym wpisywaniu nie musisz walczyć ze stałym prefiksem
            typu <code>PL1000</code> — wystarczy właściwa część numeru, zgodnie z formularzem).
          </li>
          <li>
            Uzupełnij to, co wiesz: płeć, datę urodzenia, datę przybycia, rasę, program, numer
            matki. Resztę dopiszesz później.
          </li>
          <li>
            Podaj <strong>hasło administratora</strong> i zapisz.
          </li>
        </ol>
        <p className="guide-section__text">
          Jeśli numer jest już w puli wolnych kolczyków albo przydzielony jagnięciu, aplikacja
          zapyta, czy przenieść go do stada — wtedy znika z puli na stałe.
        </p>

        <h3 className="guide-section__subtitle">Archiwum — historia, nie śmietnik</h3>
        <p className="guide-section__text">
          Sprzedaż, ubój, padnięcie — zwierzę znika z aktywnej listy, ale nie znika z pamięci
          gospodarstwa. Zakładka <strong>Archiwum zwierząt</strong> trzyma te statusy. Filtrujesz po
          kolczyku, statusie i rasie; kliknięcie numeru otwiera te same szczegóły.
        </p>
        <p className="guide-section__text">
          Przeniesienie (<strong>Przenieś do archiwum</strong>) wymaga{" "}
          <strong>hasła administratora</strong> — to celowe zabezpieczenie. Ze stada nie „przywracasz”
          sztuki jednym kliknięciem (inaczej niż grupy stanówki i wykotów, które da się przywrócić z
          archiwum rozrodu).
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/animals">
            Otwórz Stado
          </Link>
          <Link className="btn btn--ghost" to="/animals/archive">
            Archiwum
          </Link>
          <Link className="btn btn--ghost" to="/irz">
            Porównanie IRZ
          </Link>
        </p>
      </article>

      <article id="stanowka-wykoty" className="panel guide-section">
        <h2 className="guide-section__title">Stanówka i wykoty — od krycia do urodzeń</h2>
        <p className="guide-section__text">
          W menu pozycja <strong>Rozród</strong> prowadzi do hubu grup oraz zakładek{" "}
          <strong>Stanówka</strong> i <strong>Wykoty</strong>. Tworząc grupę „Stanówka i wykoty”,
          automatycznie powstaje powiązany sezon wykotów (ta sama nazwa). Możesz też utworzyć grupę
          tylko wykotów — bez pokryć. Pula numerów urzędowych jest osobno w{" "}
          <Link className="guide-inline-link" to="/resources">
            Zasobach
          </Link>
          .
        </p>

        <h3 className="guide-section__subtitle">Stanówka — wpuszczenie grupy</h3>
        <ol className="guide-steps">
          <li>
            Wejdź w{" "}
            <Link className="guide-inline-link" to="/reproduction/breeding">
              Stanówka
            </Link>{" "}
            (z menu: <strong>Rozród</strong> → <strong>Stanówka</strong>).
          </li>
          <li>
            W <strong>Rozród</strong> utwórz grupę (<strong>Nowa grupa</strong>) — wybierz
            <strong> Stanówka i wykoty</strong>, podaj nazwę, opcjonalnie rasę,{" "}
            <strong>liczbę ważeń jagniąt</strong>, datę startu, czas ciąży i notatkę. Po zapisie
            otworzy się skład grupy. Ważenia możesz też włączyć później w{" "}
            <strong>Edytuj</strong>.
          </li>
          <li>
            W panelu <strong>Edytuj grupę</strong> ustaw datę startu i czas ciąży, a także wybierz
            owce oraz tryki (wyszukiwanie po kolczyku, <strong>Zaznacz wszystkie</strong>). Na
            liście grupy jedno wspólne pole wyszukuje owce i tryki jednocześnie (tryb Ręcznie /
            Automatycznie). Planowana data wykotu = data pokrycia + czas ciąży.
          </li>
          <li>
            Po utworzeniu grupy: przypisz <strong>kolor tryka</strong> (tylko w tej grupie — np.
            czerwony, niebieski…), odnotuj <strong>pokrycie</strong> (konkretny tryk + data albo{" "}
            <strong>niepewny tryk</strong>). Możesz zaznaczyć kilka owiec na liście i jednym
            zapisem przypisać im wspólną datę krycia — z tym samym trykiem albo osobnym trykiem
            dla każdej owcy. Nad tabelami jest zwarty pasek jak na Wykotach: wyszukiwanie
            kolczyka (Ręcznie / Automatycznie) oraz filtr{" "}
            <strong>statusu krycia</strong>. Przejdź do{" "}
            <Link className="guide-inline-link" to="/reproduction/births">
              Wykotów tej grupy
            </Link>
            albo <strong>Archiwizuj</strong>.
          </li>
        </ol>
        <p className="guide-section__text">
          Jedna owca ma jedną adnotację pokrycia w grupie — kolejny zapis ją nadpisuje.
        </p>

        <h3 className="guide-section__subtitle">Wykoty — urodzenia w sezonie</h3>
        <p className="guide-section__text">
          W zakładce{" "}
          <Link className="guide-inline-link" to="/reproduction/births">
            Wykoty
          </Link>{" "}
          (subnawigacja: <strong>Aktywne</strong>, <strong>Archiwum</strong>). Wybierasz grupę z
          listy, filtrujesz po matce, płci i numerze roboczym. Na liście widać zaległe ważenia i
          status IRZ jagnięcia.
        </p>
        <ol className="guide-steps">
          <li>
            Odnotowujesz urodzenia: matka, jagnięta, płeć, numery robocze i oficjalne, wagi, notatki.
            Wpis możesz potem <strong>Edytować</strong> albo <strong>Usunąć wykot</strong>.
          </li>
          <li>
            Lista <strong>Matki bez wykotu</strong> pokazuje owce ze stanówki, które jeszcze nie
            mają wpisu.
          </li>
          <li>
            Przy matce z odnotowanym pewnym pokryciem aplikacja <strong>podpowiada ojca</strong>;
            tryki ze stanówki mają oznaczenie kolorem.
          </li>
          <li>
            <strong>Reguły ważenia</strong> (wiek w dniach albo co N dni) i{" "}
            <strong>Szybkie wagi</strong> pilnują harmonogramu. Baner sezonu pokazuje zaległe i
            najbliższy termin.
          </li>
          <li>
            Przyciskiem <strong>IRZ</strong> oznaczasz jagnię jako zarejestrowane w IRZplus (albo{" "}
            <strong>Cofnij IRZ</strong>). Po oznaczeniu numer znika z puli wolnych na zawsze — nawet
            jeśli status się zmieni.
          </li>
        </ol>

        <h3 className="guide-section__subtitle">Narzędzia w sezonie wykotów</h3>
        <ul className="guide-list">
          <li>
            <strong>Odnotuj wykot</strong> — pełny, spokojny wpis.
          </li>
          <li>
            <strong>Szybkie wykoty</strong> — kolejne odnotowania bez zamykania okna.
          </li>
          <li>
            <strong>Szybkie wagi</strong> i <strong>Reguły ważenia</strong> — ważenie bez chaosu w
            notatkach.
          </li>
          <li>
            <strong>Matki bez wykotu</strong> — lista „kogo jeszcze nie ma”.
          </li>
          <li>
            <strong>Przydziel kolczyki</strong> — oficjalne numery z puli Zasobów, gdy przychodzi na
            nie czas.
          </li>
          <li>
            <strong>Archiwizuj</strong> — przenosi sezon do archiwum.
          </li>
        </ul>
        <p className="guide-section__text">
          Starsze grupy przenosisz do <strong>Archiwum</strong>. Tam są zakładki{" "}
          <strong>Stanówki</strong> / <strong>Wykoty</strong> — grupę możesz{" "}
          <strong>Przywrócić</strong> albo trwale usunąć (usunięcie zawsze wymaga hasła
          administratora).
        </p>
        <p className="guide-section__text">
          W{" "}
          <Link className="guide-inline-link" to="/settings">
            Ustawieniach
          </Link>{" "}
          możesz włączyć opcję, żeby{" "}
          <strong>nie wymagać hasła administratora przy każdej drobnej poprawce</strong> w wykotach i
          stanówce — wygodniej w intensywnym sezonie. Trwałe usunięcie całej grupy z archiwum i tak
          wymaga hasła.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/reproduction/breeding">
            Otwórz Stanówkę
          </Link>
          <Link className="btn btn--ghost" to="/reproduction/births">
            Otwórz Wykoty
          </Link>
          <Link className="btn btn--ghost" to="/resources">
            Wolne kolczyki
          </Link>
        </p>
      </article>

      <article id="zasoby" className="panel guide-section">
        <h2 className="guide-section__title">Zasoby — pula wolnych kolczyków urzędowych</h2>
        <p className="guide-section__text">
          <Link className="guide-inline-link" to="/resources">
            Zasoby
          </Link>{" "}
          to osobna pozycja w menu (nie zakładka wykotów). Trzymasz tu numery, których jeszcze nie
          przypisałeś owcom ani jagniętom.
        </p>
        <ol className="guide-steps">
          <li>
            <strong>Dodaj do puli</strong> — wklej numery (jeden w linii albo po przecinku).
            Duplikaty i już zużyte numery aplikacja pominie i powie, ile dodała.
          </li>
          <li>
            Filtruj: <strong>Wszystkie / Wolne / Zajęte</strong> i szukaj po numerze.
          </li>
          <li>
            <strong>Usuń zajęte</strong> czyści z listy numery już użyte w stadzie lub u jagniąt
            (same zwierzęta zostają). <strong>Usuń wszystkie</strong> czyści pulę — operacja
            nieodwracalna, stado i wykoty bez zmian.
          </li>
        </ol>
        <p className="guide-section__text">
          Numer znika z puli na zawsze, gdy: przydzielisz go jagnięciu, zarejestrujesz zwierzę w
          stadzie, oznaczysz jagnię jako zarejestrowane w IRZ albo sztuka odejdzie (padnięcie /
          sprzedaż / ubój). Cofnięcie statusu IRZ <strong>nie wraca</strong> numeru do puli.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/resources">
            Otwórz Zasoby
          </Link>
        </p>
      </article>

      <article id="weryfikacja" className="panel guide-section">
        <h2 className="guide-section__title">
          Kontrole — czynności w terenie, które nie rozłażą się w zeszycie
        </h2>
        <p className="guide-section__text">
          W menu moduł nazywa się <strong>Kontrole</strong>; strona aktywnej sesji to{" "}
          <strong>Kontrola stada</strong>. Na start wybierasz rodzaj czynności. Dziś działa{" "}
          <strong>Weryfikacja kolczyków</strong>. Na liście widać też ważenie matek, szczepienia i
          odrobaczanie — na razie niedostępne („Wkrótce”). Idziesz między zwierzętami, masz telefon i
          chcesz wiedzieć: <em>ten numer jest u nas? ma oba kolczyki? czego nie sprawdziliśmy?</em>
        </p>

        <h3 className="guide-section__subtitle">Jak wygląda sesja (weryfikacja kolczyków)</h3>
        <ol className="guide-steps">
          <li>
            <Link className="guide-inline-link" to="/controls">
              Kontrole → Aktywna kontrola
            </Link>{" "}
            → wybierz <strong>Weryfikacja kolczyków</strong> → <strong>Rozpocznij kontrolę</strong>.
          </li>
          <li>
            Wybierz <strong>statusy programu</strong> owiec, które mają trafić na listę (np. całe
            stado albo tylko licencjonowane).
          </li>
          <li>
            Wprowadzaj numery w trybie <strong>Ręcznie</strong> (z prefiksem <code>PL1000</code>)
            albo <strong>Automatycznie</strong> — zależnie od tego, jak pracujesz w terenie.
          </li>
          <li>
            Kliknij <strong>Sprawdź numer</strong> — aplikacja pokaże wynik i zapyta o{" "}
            <strong>liczbę kolczyków</strong> („Czy owca ma oba kolczyki?”). Później możesz to
            poprawić.
          </li>
          <li>
            Szukaj po numerze i filtruj listę:{" "}
            <strong>Wszystkie / Zweryfikowany / Niezweryfikowany</strong>. Sortuj kolumny. W razie
            potrzeby wyczyść status pojedynczej pozycji.
          </li>
          <li>
            Na koniec: <strong>Zakończ kontrolę</strong> — krótki kreator (podsumowanie → tytuł i
            opis → <strong>Zapisz i zakończ</strong>).
          </li>
        </ol>
        <p className="guide-tip">
          <strong>Przerwa w połowie?</strong> Nie zaczynasz od zera —{" "}
          <strong>Kontynuuj kontrolę</strong>. Świadomie startujesz od nowa tylko przez{" "}
          <strong>Rozpocznij od nowa</strong>. Niedokończona sesja pojawi się też na Pulpicie w{" "}
          <strong>Do zrobienia teraz</strong>.
        </p>
        <p className="guide-section__text">
          Zakończone sesje znajdziesz w{" "}
          <Link className="guide-inline-link" to="/controls/history">
            Historii kontroli
          </Link>
          : zweryfikowane, niezweryfikowane, niezarejestrowane. Raport PDF generujesz osobno w{" "}
          <Link className="guide-inline-link" to="/reports">
            Raportach
          </Link>
          . Usunięcie sesji z historii wymaga hasła administratora.
        </p>

        <h3 className="guide-section__subtitle">Szybki słownik statusów</h3>
        <div className="guide-table-wrap">
          <ResponsiveTable className="guide-table">
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Znaczenie</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Niezarejestrowany</strong>
                </td>
                <td>Numeru nie ma w bazie stada — sprawdź literówkę albo dodaj zwierzę</td>
              </tr>
              <tr>
                <td>
                  <strong>Niezweryfikowany</strong>
                </td>
                <td>W tej sesji pozycji nie potwierdzono</td>
              </tr>
              <tr>
                <td>Zarejestrowany / zweryfikowany</td>
                <td>Numer jest w bazie i (w zależności od kroku) potwierdzony w kontroli</td>
              </tr>
            </tbody>
          </ResponsiveTable>
        </div>
        <p className="guide-actions">
          <Link className="btn" to="/controls">
            Rozpocznij kontrolę
          </Link>
          <Link className="btn btn--ghost" to="/controls/history">
            Historia kontroli
          </Link>
        </p>
      </article>

      <article id="raporty" className="panel guide-section">
        <h2 className="guide-section__title">Raporty — wynik, który możesz przekazać dalej</h2>
        <p className="guide-section__text">
          Raport to spokojne domknięcie pracy: otwierasz PDF w przeglądarce albo{" "}
          <strong>zapisujesz lokalnie</strong> na urządzeniu. Strona w menu to{" "}
          <strong>Raporty</strong>, nagłówek — <strong>Generowanie raportów</strong>.
        </p>
        <p className="guide-section__text">
          <strong>Raport z weryfikacji kolczyków:</strong> kończysz sesję →{" "}
          <Link className="guide-inline-link" to="/reports">
            Raporty
          </Link>{" "}
          → wybierasz sesję z listy → <strong>Otwórz raport PDF</strong> albo{" "}
          <strong>Zapisz lokalnie</strong> — dokument do archiwum gospodarstwa, kontroli albo drugiej
          osoby. Na liście widać, ile pozycji, ile zweryfikowanych i ile niezarejestrowanych.
        </p>
        <p className="guide-section__text">
          Inne rodzaje raportów w tym miejscu pojawią się później — m.in. z ważeń, szczepień i
          odrobaczeń. Pełna lista planowanych rozszerzeń jest w sekcji{" "}
          <a className="guide-inline-link" href="#planowane">
            Planowane rozszerzenia
          </a>
          .
        </p>
        <p className="guide-section__text">
          <strong>Raport pojedynczego zwierzęcia:</strong> w <strong>Szukaj owcy</strong> po
          znalezieniu owcy możesz pobrać PDF z jej historią i danymi — przydatne przy sprzedaży,
          weterynarzu albo własnej dokumentacji.
        </p>
        <p className="guide-tip">
          Jeśli przeglądarka blokuje nowe okno, użyj <strong>Zapisz lokalnie</strong> — efekt ten
          sam, bez walki z popupami.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/reports">
            Otwórz Raporty
          </Link>
        </p>
      </article>

      <article id="finanse" className="panel guide-section">
        <h2 className="guide-section__title">Finanse hodowli — pieniądze gospodarstwa obok stada</h2>
        <p className="guide-section__text">
          Pasza, weterynarz, sprzedaż, sprzęt — wszystko ląduje w jednym rejestrze zamiast w trzech
          aplikacjach bankowych i szufladzie z paragonami. W menu pozycja nazywa się{" "}
          <strong>Finanse</strong>, a strona — <strong>Finanse hodowli</strong>.
        </p>

        <h3 className="guide-section__subtitle">Co zapisujesz</h3>
        <ul className="guide-list">
          <li>
            <strong>Wydatki</strong> i <strong>przychody</strong>
          </li>
          <li>kwotę, datę, kategorię (i podkategorię)</li>
          <li>notatkę</li>
          <li>
            opcjonalnie <strong>zdjęcie</strong> (np. paragon)
          </li>
          <li>
            opcjonalnie przypisanie: <strong>Brak</strong> (koszt ogólny),{" "}
            <strong>Wybrane</strong> owce albo <strong>Całe stado</strong>
          </li>
        </ul>

        <h3 className="guide-section__subtitle">Jak dodać wpis</h3>
        <ol className="guide-steps">
          <li>
            <Link className="guide-inline-link" to="/expenses">
              Finanse
            </Link>{" "}
            → <strong>Dodaj wydatek</strong> albo <strong>Dodaj przychód</strong>.
          </li>
          <li>Uzupełnij pola.</li>
          <li>
            W razie potrzeby <strong>Dodaj zdjęcie</strong> i powiązanie ze zwierzętami (Wybrane /
            Całe stado).
          </li>
          <li>Zapisz — nowy wpis nie wymaga hasła administratora.</li>
        </ol>
        <p className="guide-section__text">
          Własne kategorie i podkategorie dodasz przez <strong>+ Nowa</strong>. Listę filtrujesz:
          typ (wszystkie / wydatki / przychody). Podsumowanie (łączny przychód, łączny wydatek,
          czysty zysk / bilans) przełączasz okresem: <strong>Ten miesiąc</strong>,{" "}
          <strong>Kwartał</strong>, <strong>Rok</strong>. Wykres{" "}
          <strong>Struktury wydatków</strong> albo <strong>Struktury przychodów</strong> pokazuje,{" "}
          <strong>na co idą pieniądze</strong> — bez ręcznego sumowania w Excelu.
        </p>
        <p className="guide-section__text">
          Edycja i usunięcie wpisu wymaga <strong>hasła administratora</strong>. Skrót miesiąca
          widzisz też na Pulpicie (sekcja <strong>Ten miesiąc</strong>). Wpisów dodanych offline
          aplikacja kolejkuje i synchronizuje po powrocie sieci.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/expenses">
            Otwórz Finanse
          </Link>
        </p>
      </article>

      <article id="irz" className="panel guide-section">
        <h2 className="guide-section__title">IRZ — most między gospodarstwem a IRZplus</h2>
        <p className="guide-section__text">
          Moduł{" "}
          <Link className="guide-inline-link" to="/irz">
            IRZ
          </Link>{" "}
          służy do pracy z danymi z systemu IRZplus: logowanie, pobranie / porównanie listy, import,
          synchronizacja szczegółów (zdarzenia, przemieszczenia). Na stronie jest też przycisk{" "}
          <strong>Jak działa IRZ</strong> — ten sam opis, pod ręką przy pracy.
        </p>

        <h3 className="guide-section__subtitle">Na co zwrócić uwagę</h3>
        <ul className="guide-list">
          <li>
            Jeśli nie ma aktywnej sesji — uzupełnij dane w <strong>Logowanie do IRZplus</strong>{" "}
            (numer producenta i hasło).
          </li>
          <li>
            Hasło IRZ jest szyfrowane <strong>lokalnie w Twojej przeglądarce</strong>; sesja IRZ na
            serwerze nie przechowuje Twojego hasła w postaci jawnej.
          </li>
          <li>
            <strong>Pobierz listę</strong> — podgląd danych z IRZplus, bez zapisu.{" "}
            <strong>Zapisz do bazy (scal)</strong> albo <strong>Zastąp całą bazę</strong> — wybierz
            świadomie: scalenie aktualizuje istniejące rekordy, zastąpienie nadpisuje lokalne dane
            listą z IRZ (nieodwracalne).
          </li>
          <li>
            Przy scaleniu konflikty (kolczyki zmienione u Ciebie) mogą zostać pominięte — wtedy
            okno konfliktów pozwala <strong>Nadpisać wszystkie konflikty</strong>.
          </li>
          <li>
            <strong>Synchronizuj szczegóły</strong> — zdarzenia, przemieszczenia i środki
            identyfikacji zwierząt już w bazie (to widać potem w szczegółach zwierzęcia).
          </li>
          <li>
            <strong>Porównaj ze stadem</strong> — różnice: tylko w IRZ, tylko w aplikacji, zmienione
            — bez automatycznego zapisu.
          </li>
          <li>
            <strong>Wyloguj IRZ</strong> kończy sesję IRZplus (nie wylogowuje Cię z AgroHodowcy).
          </li>
          <li>Import IRZ obejmuje obecnie owce — inne gatunki z systemu są pomijane.</li>
        </ul>
        <p className="guide-callout">
          Typowy przepływ: zaloguj → pobierz listę → scal (lub zastąp) → synchronizuj szczegóły →
          ewentualnie porównaj ze stadem. Cel: mniej przepisywania i mniej „a u nas w IRZ jest
          inaczej”.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/irz">
            Otwórz IRZ
          </Link>
        </p>
      </article>

      <article id="planowane" className="panel guide-section">
        <h2 className="guide-section__title">Planowane rozszerzenia — co dodamy w najbliższej przyszłości</h2>
        <p className="guide-section__text">
          AgroHodowca rozwija się na podstawie pracy hodowców w terenie. Poniżej lista funkcji, nad
          którymi pracujemy — część z nich widać już w aplikacji z adnotacją „Wkrótce” (np. ważenie,
          szczepienia i odrobaczanie w module Kontrole). Kolejność wdrożenia może się zmienić w
          zależności od priorytetów i integracji z IRZplus.
        </p>

        {PLANNED_FEATURE_GROUPS.map((group) => (
          <div key={group.title}>
            <h3 className="guide-section__subtitle">{group.title}</h3>
            <ul className="guide-list">
              {group.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}

        <p className="guide-callout">
          Masz sugestię albo pilną potrzebę? Napisz na{" "}
          <a className="guide-inline-link" href={CONTACT_MAILTO}>
            {CONTACT_EMAIL}
          </a>{" "}
          — feedback z gospodarstw pomaga ustalać kolejność prac.
        </p>
      </article>

      <article id="ustawienia" className="panel guide-section">
        <h2 className="guide-section__title">
          Ustawienia, MFA i bezpieczeństwo — spokój przy ważnych decyzjach
        </h2>
        <p className="guide-section__text">
          Nie każda czynność w gospodarstwie jest „kliknij i zapomnij”. Archiwizacja, usuwanie
          historii kontroli czy poważniejsze zmiany w wykotach mogą wymagać{" "}
          <strong>osobnego hasła administratora</strong>.
        </p>
        <p className="guide-section__text">
          W{" "}
          <Link className="guide-inline-link" to="/settings">
            Ustawieniach
          </Link>{" "}
          masz:
        </p>
        <ul className="guide-list">
          <li>
            <strong>Hasło logowania</strong> — ustawiasz lub zmieniasz hasło do konta (osobne od
            logowania Google). Minimum 8 znaków, inne niż hasło administratora.
          </li>
          <li>
            <strong>Uwierzytelnianie dwuskładnikowe (MFA)</strong> — dodatkowy kod z aplikacji typu
            Google Authenticator albo Authy. Po konfiguracji: zeskanuj kod QR (albo wpisz klucz
            ręczny), <strong>zapisz kody zapasowe</strong> (każdy działa raz) i potwierdź kodem z
            aplikacji. Przy logowaniu hasłem aplikacja poprosi o kod MFA. Wyłączenie MFA wymaga
            hasła logowania.
          </li>
          <li>
            <strong>Hasło administratora</strong> — ustawiasz po raz pierwszy albo zmieniasz
            istniejące.
          </li>
          <li>
            <strong>Potwierdzanie operacji hasłem</strong> — możesz wyłączyć wymaganie hasła
            administratora przy zwykłej edycji wykotów i stanówki. Zapis tej opcji wymaga hasła
            logowania. Trwałe usunięcie grupy z archiwum zawsze wymaga hasła administratora.
          </li>
          <li>
            <strong>Wyczyść zawartość aplikacji</strong> — usuwa stado, wykoty, stanówki, finanse i
            pozostałe dane gospodarstwa; konto zostaje. Przez 14 dni możesz{" "}
            <strong>Przywrócić dane</strong> z kopii zapasowej (hasło administratora + fraza
            potwierdzająca przy czyszczeniu).
          </li>
          <li>
            <strong>Usunięcie konta</strong> — konto jest dezaktywowane, a po 14 dniach trwale
            usuwane. Link anulowania przychodzi na Twój e-mail.
          </li>
        </ul>
        <p className="guide-section__text">
          <strong>Hasło administratora</strong> chroni m.in.: dodawanie i edycję zwierząt,
          archiwizację, edycję/usuwanie wpisów finansowych, usuwanie historii kontroli. Reguły:
        </p>
        <ul className="guide-list">
          <li>
            należy do <strong>Twojego konta</strong>,
          </li>
          <li>
            powinno mieć <strong>co najmniej 8 znaków</strong>,
          </li>
          <li>
            <strong>nie może być takie samo</strong> jak hasło logowania.
          </li>
        </ul>
        <p className="guide-section__text">
          To nie jest utrudnianie życia — to hamulec przed przypadkowym skasowaniem historii, której
          potem nie odtworzysz z pamięci.
        </p>
        <p className="guide-section__text">
          Dane gospodarstwa są chronione sesją: bez logowania nie wejdziesz w chronione ekrany. Po
          wielu błędnych hasłach logowanie może się na chwilę zablokować — to ochrona, nie kaprys
          systemu.
        </p>
        <p className="guide-actions">
          <Link className="btn" to="/settings">
            Otwórz Ustawienia
          </Link>
        </p>
      </article>

      <article id="offline" className="panel guide-section">
        <h2 className="guide-section__title">Telefon, teren i tryb offline</h2>
        <p className="guide-section__text">
          Aplikacja jest <strong>PWA</strong> — możesz dodać ją do ekranu głównego telefonu lub
          komputera i otwierać jak zwykłą apkę. Na Androidzie w Chrome: menu →{" "}
          <strong>Zainstaluj aplikację</strong> / <strong>Dodaj do ekranu głównego</strong>. Na
          iPhonie w Safari: Udostępnij → <strong>Na ekranie początkowym</strong>.
        </p>
        <p className="guide-tip">
          <strong>Przed wyjazdem w teren:</strong> zaloguj się przy dobrym internecie — aplikacja
          pobierze dane do pracy offline. W belce kliknij ikonę <strong>Status połączenia</strong>{" "}
          (prawy górny róg) i w razie potrzeby <strong>Synchronizuj dane offline</strong>. Gdy nie
          ma sieci, w menu zobaczysz napis <strong>Tryb offline</strong>.
        </p>
        <p className="guide-section__text">
          <strong>Logowanie bez sieci:</strong> po pierwszym zalogowaniu online aplikacja zapamiętuje
          dane na urządzeniu. Offline możesz odblokować konto tym samym e-mailem i hasłem (
          <strong>Odblokuj offline</strong>). Jeśli nigdy nie logowałeś się online na tym telefonie
          — najpierw potrzebujesz sieci. Google i reset hasła działają tylko online.
        </p>

        <h3 className="guide-section__subtitle">Co działa bez internetu?</h3>
        <p className="guide-section__text">
          <strong>Wszystkie główne funkcje aplikacji działają w pełni offline:</strong>
        </p>
        <ul className="guide-list">
          <li>
            <strong>Weryfikacja kolczyków</strong> — rozpocznij sesję, skanuj całe stado, wszystko
            zapisuje się lokalnie
          </li>
          <li>
            <strong>Wykoty i wagi</strong> — rejestruj narodziny jagniąt, zapisuj wagi, dodawaj matki
          </li>
          <li>
            <strong>Finanse</strong> — dodawaj wydatki i przychody
          </li>
          <li>
            <strong>Zwierzęta</strong> — twórz, edytuj i archiwizuj zwierzęta
          </li>
          <li>
            <strong>Zadania</strong> — zarządzaj listą TODO
          </li>
          <li>
            <strong>Wszystkie listy i przeglądanie</strong> — dostęp do danych stada, statystyk,
            historii
          </li>
        </ul>

        <h3 className="guide-section__subtitle">Jak to działa?</h3>
        <p className="guide-section__text">
          Zmiany zapisują się w <strong>lokalnej kolejce</strong> i automatycznie synchronizują, gdy
          wrócisz do zasięgu. Synchronizacja działa przy:
        </p>
        <ul className="guide-list">
          <li>starcie aplikacji (jeśli masz internet),</li>
          <li>powrocie do zasięgu (automatycznie wykrywa),</li>
          <li>
            ręcznie — ikona połączenia → <strong>Wyślij zmiany</strong> (kolejka) albo{" "}
            <strong>Synchronizuj dane offline</strong> (odświeżenie lokalnej kopii).
          </li>
        </ul>
        <p className="guide-section__text">
          Jeśli zapis wymaga ponownego hasła administratora (np. po zamknięciu karty), status
          połączenia poprosi o nie przyciskiem <strong>Wyślij z hasłem</strong>. Aktualizacja
          aplikacji (PWA) wymaga sieci; przy niezsynchronizowanych zmianach w kolejce aktualizacja
          poczeka.
        </p>

        <h3 className="guide-section__subtitle">Hasło administratora offline</h3>
        <p className="guide-section__text">
          Operacje wymagające hasła (np. tworzenie zwierząt, archiwizacja) działają offline. Po
          pierwszym użyciu hasła online aplikacja zapisuje zaszyfrowaną wersję do weryfikacji
          lokalnej. Przy synchronizacji serwer sprawdzi hasło ponownie.
        </p>

        <h3 className="guide-section__subtitle">Status połączenia</h3>
        <p className="guide-section__text">
          W prawym górnym rogu belki masz <strong>ikonę statusu</strong>:
        </p>
        <ul className="guide-list">
          <li>🟢 <strong>Online</strong> — połączenie działa, zmiany synchronizują się na bieżąco</li>
          <li>🔴 <strong>Offline</strong> — brak internetu, zmiany zapisują się lokalnie</li>
          <li>🟠 <strong>Synchronizacja...</strong> — wysyłanie zmian na serwer</li>
        </ul>
        <p className="guide-section__text">
          Kliknij ikonę, aby zobaczyć szczegóły: wiek lokalnych danych, liczbę zmian w kolejce i
          przyciski do ręcznej synchronizacji.
        </p>

        <p className="guide-callout">
          <strong>Ważne:</strong> dane lokalne są ważne przez 7 dni. Po tym czasie aplikacja
          odświeży je przy następnym połączeniu. Sesja logowania też wygasa po 7 dniach — będziesz
          musiał zalogować się ponownie.
        </p>
      </article>

      <article id="dni" className="panel guide-section">
        <h2 className="guide-section__title">Typowe dni z aplikacją</h2>
        <div className="guide-scenarios">
          <div className="guide-scenario">
            <h3 className="guide-scenario__title">Poranek przy kawie</h3>
            <p className="guide-section__text">
              Pulpit → szybki rzut oka na kafelki, Do zrobienia teraz i zadania → ewentualnie Szukaj
              owcy albo jedna transakcja z wczoraj.
            </p>
          </div>
          <div className="guide-scenario">
            <h3 className="guide-scenario__title">Sezon rozrodu</h3>
            <p className="guide-section__text">
              Stanówka (skład, kolory tryków, pokrycia) → Wykoty (urodzenia, wagi, matki bez wykotu)
              → Zasoby i przydział kolczyków urzędowych → oznaczenie IRZ przy jagniętach.
            </p>
          </div>
          <div className="guide-scenario">
            <h3 className="guide-scenario__title">Kontrola stada</h3>
            <p className="guide-section__text">
              Kontrole → wybór czynności (dziś: weryfikacja kolczyków) → sesja w terenie → przerwa /
              wznowienie → zakończenie. Raport PDF osobno w Raportach.
            </p>
          </div>
          <div className="guide-scenario">
            <h3 className="guide-scenario__title">Koniec miesiąca</h3>
            <p className="guide-section__text">
              Finanse hodowli → filtry okresu → wykres struktury → ewentualnie raporty na potrzeby
              dokumentacji. Statystyki stada, gdy chcesz liczby sezonu.
            </p>
          </div>
          <div className="guide-scenario">
            <h3 className="guide-scenario__title">Porządek z IRZ</h3>
            <p className="guide-section__text">
              IRZ → zaloguj → pobierz listę → scal albo porównaj różnice → synchronizuj szczegóły.
              Potem szczegóły zwierzęcia w stadzie mają historię zdarzeń i przemieszczeń.
            </p>
          </div>
        </div>
      </article>

      <article id="faq" className="panel guide-section">
        <h2 className="guide-section__title">Częste pytania</h2>

        <details className="guide-faq">
          <summary>Nie mogę się zalogować / „za dużo prób”</summary>
          <p className="guide-section__text">
            Odczekaj chwilę i spróbuj ponownie. Przy Google wybierz <strong>to samo konto</strong>,
            którego użyłeś przy rejestracji. Przy e-mailu sprawdź, czy kliknąłeś link aktywacyjny.
            Hasło możesz zresetować linkiem <strong>Zapomniałem hasła</strong>. Jeśli włączyłeś MFA
            — potrzebujesz kodu z aplikacji albo kodu zapasowego.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Zgubiłem telefon z aplikacją uwierzytelniającą (MFA)</summary>
          <p className="guide-section__text">
            Przy logowaniu wpisz jeden z <strong>kodów zapasowych</strong> zapisanych przy
            konfiguracji MFA. Każdy kod działa tylko raz. Potem w Ustawieniach możesz wyłączyć MFA
            (hasło logowania) i skonfigurować je od nowa.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Kolczyk „Niezarejestrowany”</summary>
          <p className="guide-section__text">
            Numeru nie ma w bazie. Sprawdź literówkę. Jeśli kolczyk jest poprawny — dodaj zwierzę w{" "}
            <Link className="guide-inline-link" to="/animals">
              Zwierzętach
            </Link>{" "}
            i dokończ weryfikację.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Gdzie są wolne kolczyki? Nie widzę ich w Wykotach</summary>
          <p className="guide-section__text">
            To osobna pozycja menu:{" "}
            <Link className="guide-inline-link" to="/resources">
              Zasoby
            </Link>
            . W Wykotach zostaje przycisk <strong>Przydziel kolczyki</strong>, który korzysta z tej
            puli.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Przerwałem kontrolę — czy zaczynam od zera?</summary>
          <p className="guide-section__text">
            Nie. <strong>Kontynuuj kontrolę</strong>. Od zera zaczynasz tylko wtedy, gdy świadomie
            czyścisz aktywną sesję albo startujesz nową.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Dane wyglądają nieaktualnie</summary>
          <p className="guide-section__text">
            Odśwież stronę. Offline? Poczekaj na sync po powrocie sieci. Zerknij na ikonę statusu
            połączenia w belce. Jeśli problem wraca — zgłoś (patrz niżej).
          </p>
        </details>
        <details className="guide-faq">
          <summary>Nie mogę się zalogować bez internetu</summary>
          <p className="guide-section__text">
            Najpierw zaloguj się <strong>online przynajmniej raz</strong> na tym urządzeniu. Potem
            offline działa odblokowanie tym samym e-mailem i hasłem. Google i reset hasła wymagają
            sieci.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Czy potrzebuję wiedzy informatycznej?</summary>
          <p className="guide-section__text">
            Nie. Przeglądarka, podstawowa obsługa telefonu lub komputera i ten przewodnik wystarczą.
            Hasła do wrażliwych operacji ustala użytkownik konta.
          </p>
        </details>
        <details className="guide-faq">
          <summary>Skąd wziąć hasło administratora?</summary>
          <p className="guide-section__text">
            Ustawiasz je sam w{" "}
            <Link className="guide-inline-link" to="/settings">
              Ustawieniach
            </Link>{" "}
            — to osobne hasło od logowania do aplikacji. Potrzebujesz go przy wrażliwych operacjach
            (np. edycja zwierząt, archiwizacja, edycja finansów).
          </p>
        </details>
        <details className="guide-faq">
          <summary>Co z danymi przykładowymi?</summary>
          <p className="guide-section__text">
            Służą do nauki i testów. Wczytujesz je przyciskiem <strong>Przykłady</strong> w belce
            aplikacji. Istniejące zwierzęta nie zostaną nadpisane. Cały zestaw możesz później usunąć.
          </p>
        </details>
      </article>

      <article id="pomoc" className="panel guide-section">
        <h2 className="guide-section__title">Gdy coś nie gra</h2>
        <ol className="guide-steps">
          <li>
            Sprawdź status połączenia w belce i czy nie jesteś w trybie offline.
          </li>
          <li>
            <strong>Zgłoszenie</strong> — napisz krótko: <em>co robiłeś</em>,{" "}
            <em>czego oczekiwałeś</em>, <em>co się stało</em>, na jakim urządzeniu / w jakiej
            przeglądarce. Zrzut ekranu bardzo pomaga.
          </li>
          <li>
            <strong>Kanał kontaktu</strong> — napisz do administratora na{" "}
            <a className="guide-inline-link" href={CONTACT_MAILTO}>
              {CONTACT_EMAIL}
            </a>
            .
          </li>
        </ol>
      </article>

      <article id="checklista" className="panel guide-section">
        <h2 className="guide-section__title">Na dobry początek — checklista</h2>
        <ul className="guide-checklist">
          {CHECKLIST_ITEMS.map((item) => {
            const done = Boolean(checklistDone[item.id]);
            return (
              <li key={item.id} className={done ? "is-done" : undefined}>
                <label className="guide-checklist__label">
                  <input
                    type="checkbox"
                    checked={done}
                    onChange={() => toggleChecklistItem(item.id)}
                  />
                  <span>{item.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
        <p className="guide-closing">Powodzenia przy stadzie — w oborze i w aplikacji.</p>
      </article>
    </main>
  );
}
